import { z } from 'zod';
import superjson from 'superjson';

// ── New patch-based schema ──
// The autofix endpoint now receives file contents + error context and returns
// import patches to apply, instead of generating entire new component files.

export const schema = z.object({
  sessionId: z.string().min(1),
  // Primary file that triggered the error
  filePath: z.string().min(1),
  fileContents: z.string(),
  // Error context from bundler/preview
  errorMessage: z.string().min(1),
  bundlerLogs: z.string().optional(),
  // Framework context
  framework: z.string().optional(),
  // All file names in the session (for cross-file resolution)
  existingFileNames: z.array(z.string()).optional(),
  // --- Legacy fields (still accepted for backward compat) ---
  componentName: z.string().optional(),
});

export type InputType = z.infer<typeof schema>;

export type Patch = {
  filePath: string;
  before: string;
  after: string;
};

export type OutputType = {
  patches: Patch[];
  summary: string;
  remainingIssues: string;
  // Legacy fields for backward compat
  fileName?: string;
  filePath?: string;
  content?: string;
};

// Cooldown tracking
let autofixCooldownUntil = 0;
let autofixConsecutiveFailures = 0;
const AUTOFIX_COOLDOWN_MS = 60_000;
const AUTOFIX_MAX_COOLDOWN_MS = 300_000;

export const autoFixComponent = async (
  body: InputType,
  init?: RequestInit,
): Promise<OutputType | null> => {
  if (Date.now() < autofixCooldownUntil) return null;

  try {
    const validatedInput = schema.parse(body);
    const result = await fetch(`/_api/ai/autofix`, {
      method: 'POST',
      credentials: 'include',
      body: superjson.stringify(validatedInput),
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    });
    if (!result.ok) {
      autofixConsecutiveFailures++;
      const backoff = Math.min(
        AUTOFIX_COOLDOWN_MS * Math.pow(2, autofixConsecutiveFailures - 1),
        AUTOFIX_MAX_COOLDOWN_MS,
      );
      autofixCooldownUntil = Date.now() + backoff;
      console.debug(
        `[autofix] Error ${result.status} — cooldown ${Math.round(backoff / 1000)}s (failure #${autofixConsecutiveFailures})`,
      );
      return null;
    }
    autofixConsecutiveFailures = 0;
    return superjson.parse<OutputType>(await result.text());
  } catch (err) {
    autofixConsecutiveFailures++;
    const backoff = Math.min(
      AUTOFIX_COOLDOWN_MS * Math.pow(2, autofixConsecutiveFailures - 1),
      AUTOFIX_MAX_COOLDOWN_MS,
    );
    autofixCooldownUntil = Date.now() + backoff;
    console.debug('[autofix] Request failed:', err instanceof Error ? err.message : err);
    return null;
  }
};
